import SvgIcon from '../SvgIcon/SvgIcon';
import { HTMLInputTypeAttribute, MouseEvent, useState } from 'react';
import {
  getInputButtonClassList,
  getInputIconColour,
  getInputWrapperClassList,
} from '../../utils/input/input';
import './Input.scss';
import { svgReferences } from '../../utils/svg/svg';
import Text from '../Text/Text';

type InputType = {
  inputId: string;
  inputType?: HTMLInputTypeAttribute;
  label?: string;
  value?: number | string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  placeholder?: string;
  withIconButton?: boolean;
  iconButtonName?: keyof typeof svgReferences;
  countryCode?: string;
  leftIconName?: keyof typeof svgReferences;
  rightIconName?: keyof typeof svgReferences;
  inactive?: boolean;
  readOnly?: boolean;
  error?: string;
  autoFocus?: boolean;
  autoComplete?: string;
  tabIndex?: number;
  buttonTabIndex?: number;
  small?: boolean;
  noInputBoxOutline?: boolean;
  leftIconColor?: string | null;
  orangeIconColor?: boolean;
  leftIconSize?: number;
  withSecondaryIconButton?: boolean;
  secondaryIconButtonName?: keyof typeof svgReferences;
  handleIconButtonClick?: () => void;
  disabled?: boolean;
  required?: boolean;
  onClear?: () => void;
  showClearButton?: (v: number | string) => boolean;
  customOnClick?: (v: unknown) => void;
  applyDisableInputStyling?: boolean;
  minsPlaceholder?: boolean;
  maxLength?: number;
  inputMode?:
    | 'none'
    | 'text'
    | 'tel'
    | 'url'
    | 'email'
    | 'numeric'
    | 'decimal'
    | 'search'
    | undefined;
};

const Input = ({
  inputId,
  inputType = 'text',
  label,
  value = '',
  onChange,
  placeholder = '',
  withIconButton = false,
  iconButtonName,
  countryCode,
  leftIconName,
  rightIconName,
  inactive = false,
  readOnly = false,
  orangeIconColor = false,
  leftIconColor = null,
  error = '',
  autoFocus = false,
  autoComplete = 'off',
  tabIndex = 0,
  buttonTabIndex = -1,
  small = false,
  noInputBoxOutline = false,
  leftIconSize = 12,
  secondaryIconButtonName,
  handleIconButtonClick,
  disabled = false,
  required = false,
  onClear,
  showClearButton = () => false,
  customOnClick,
  // applyDisableInputStyling = false,
  minsPlaceholder = false,
  maxLength,
  inputMode,
}: InputType) => {
  /* this is put in place for the specific use case of preventing browser from showing password suggestions
    we can disable password auto fill autoComplete="new-password"
    but this does not remove the password suggestion box in browsers
    so the solution is to first set inputType to "text", then onFocus or onInput change to "password", which circumvents the suggestion box */
  const [overrideInputType, setOverrideInputType] = useState(inputType);
  const changeTypeInput = () => {
    if (inputType === 'new-password') setOverrideInputType('password');
  };

  const handleEyePress = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    setOverrideInputType(prev => (prev === 'password' ? 'text' : 'password'));
  };

  return (
    <div className="input-container">
      {label && (
        <label htmlFor={inputId} className="body-small">
          <Text type="body" size="small">
            {`${label} ${required ? '*' : ''}`}
          </Text>
        </label>
      )}
      <div className={`input-horizontal-container ${disabled && 'disabled-background'}`}>
        <div
          className={getInputWrapperClassList(
            `input-wrapper body ${disabled && 'disabled-styling'}`,
            readOnly,
            inactive,
            error,
            withIconButton,
            noInputBoxOutline,
            disabled,
          )}
        >
          {countryCode && (
            <div className="input-left-country-code">
              <Text type="body">{countryCode}</Text>
            </div>
          )}
          {leftIconName && (
            <div className={`input-left-icon ${disabled && 'input-left-icon-grey-background'}`}>
              <SvgIcon
                data-testid={`${leftIconName}-svg`}
                iconName={leftIconName}
                fill={getInputIconColour(inactive, readOnly, orangeIconColor, leftIconColor)}
                width={leftIconSize}
                height={leftIconSize}
              />
            </div>
          )}
          <input
            id={inputId}
            data-testid={inputId}
            name={inputId}
            type={overrideInputType}
            className={`${countryCode && 'with-country-code'} ${small && 'smaller-height'} ${
              minsPlaceholder && 'eighty-five-percent-width'
            }`}
            placeholder={`${placeholder}${required ? ' (required)' : ''}`}
            readOnly={readOnly || inactive ? true : false}
            value={value}
            onChange={onChange}
            autoFocus={autoFocus}
            autoComplete={autoComplete}
            onFocus={changeTypeInput}
            onInput={changeTypeInput}
            tabIndex={tabIndex}
            min="0"
            max="59"
            disabled={disabled}
            onClick={val => customOnClick?.(val)}
            maxLength={maxLength}
            inputMode={inputMode}
          />
          {inputType === 'password' && (
            <div className="input-eye" onClick={handleEyePress}>
              <SvgIcon
                iconName={overrideInputType === 'password' ? 'Eye' : 'EyeSlash'}
                data-testid={`eye-svg`}
                fill={getInputIconColour(inactive, readOnly)}
                width={24}
                height={24}
              />
            </div>
          )}
          {rightIconName && (
            <div className="input-right-icon">
              <SvgIcon
                iconName={rightIconName}
                data-testid={`${rightIconName}-svg`}
                fill={getInputIconColour(inactive, readOnly)}
                width={12}
                height={12}
              />
            </div>
          )}
          {showClearButton && showClearButton(value) && (
            <div className="clear-button-container">
              <div className="clear-button" onClick={onClear}>
                <SvgIcon iconName="Xmark" width={10} height={10} />
              </div>
            </div>
          )}
        </div>
        {secondaryIconButtonName && (
          <button
            type="button"
            className={getInputButtonClassList(
              'input-icon-button-secondary',
              readOnly,
              inactive,
              error,
            )}
            tabIndex={buttonTabIndex}
            disabled={readOnly}
          >
            <SvgIcon
              data-testid={`${secondaryIconButtonName}-svg`}
              iconName={secondaryIconButtonName}
              fill={getInputIconColour(inactive, readOnly)}
              width={16}
              height={16}
            />
          </button>
        )}
        {iconButtonName && (
          <button
            type="button"
            className={getInputButtonClassList('input-icon-button-main', readOnly, inactive, error)}
            tabIndex={buttonTabIndex}
            onClick={() => {
              handleIconButtonClick?.();
            }}
            disabled={readOnly}
          >
            <SvgIcon
              data-testid={`${iconButtonName}-svg`}
              iconName={iconButtonName}
              fill={getInputIconColour(inactive, readOnly)}
              width={16}
              height={16}
            />
          </button>
        )}
      </div>
      {error && (
        <Text size="small" type="body" className="error-message">
          {error}
        </Text>
      )}
    </div>
  );
};

export default Input;
